// tslint:disable:max-file-line-count
import Bluebird from 'bluebird';
import * as $ from 'jquery';
import moment from 'moment';
import { findAny, flatMap } from '../../../../../services/arrays';
import queryfy from '../../../../../services/queryfy';
import { AjaxError } from '../../../services/ajax';
import { apiCall } from '../../../services/api';
import { getBillingAccountId, getSessionID, isExternal, pageLang } from '../../../services/app-storage';
import { encodeBase64 } from '../../../services/base64';
import { setSessionItem } from '../../../services/browser-storage';
import host from '../../../services/current-host';
import onMessage from '../../../services/external-messages';
import { $id } from '../../../services/jquery-utils';
import { getUrlParam } from '../../../services/url-params';
import fetchUserConfig from '../../../services/user-config';
import getErrorMessages from '../errors/error-storage';
import { errorsExisting, removeGlobalError, setGlobalError, updateErrorView } from '../errors/errors';
import { getWidgetForm, IWidgetFormRow } from '../models/form';
import { getFlightInfo, getPlaceInfo, getTransportInfo } from '../quote-data';
import saveSession from '../session/session-save';
import { currentMode, returnMode } from '../storage';
import { validateRow } from '../validation/validator';
import { IFlight, Point } from './quote-params';
import createTrips from './trip-creator';

let inProgress = false;
let $searchBtn: JQuery;

interface IBookingData { PickupTimeZulu?: string;
                        PickupTimeLocal?: string;
                        DropOffTimeZulu?: string;
                        DropOffTimeLocal?: string;
}

export function loadSubmit(): void {
    $searchBtn = $id('searchButton');

    $searchBtn.on('click', event => {
        event.preventDefault();
        submitForm();
    });
}

function setInProgress(value: boolean): void {
    $searchBtn.toggleClass('loading', value);
    inProgress = value;
}

let submitting = Bluebird.resolve();
export function submitForm(): Bluebird<void> {
    saveSession();

    if (inProgress) return submitting;
    setInProgress(true);
    return submitting = submit();
}

async function submit(): Bluebird<void> {
    removeGlobalError();

    const billingRow = document.getElementById('billingAccountRow');
    const billingSelect = document.getElementById('billingAccountSwitcher') as HTMLSelectElement | null;
    const billingRowVisible = billingRow !== null && !billingRow.hasAttribute('hidden');
    if (billingRowVisible && getBillingAccountId() === null) {
        if (billingSelect) billingSelect.classList.add('error');
        const messages = await getErrorMessages();
        setGlobalError(messages.other.incomplete);
        setInProgress(false);
        return updateErrorView();
    }

    // Run validations
    const form = getWidgetForm();
    const validations = [validateRow(form.row1, currentMode.value, form.noFlightCheck.prop('checked'), false)];
    if (returnMode.value) {
        // Also check return route
        validations.push(validateRow(form.row2, currentMode.value, form.noFlightCheck.prop('checked'), true));
    }

    // Check validation results
    const results = await Bluebird.all(validations);
    if (findAny(results, valid => !valid)) {
        // Invalid fields found
        if (!errorsExisting()) {
            // There must be empty fields, show error
            const messages = await getErrorMessages();
            setGlobalError(messages.other.incomplete);
        }

        setInProgress(false);
        return updateErrorView();
    } else if (returnMode.value) {
        return apiSubmit(form.noFlightCheck.prop('checked'), form.row1, form.row2);
    } else {
        return apiSubmit(form.noFlightCheck.prop('checked'), form.row1);
    }
}

function apiSubmit(noFlight: boolean, row1: IWidgetFormRow, row2?: IWidgetFormRow): Bluebird<void> {
    // Create trips
    const routes: Array<{ SortNumber: number, Trips: API_CreateBooking.Trip[] }> = [{
        SortNumber: 0,
        Trips: createTrips(row1, noFlight)
    }];
    if (row2) {
        routes.push({
            SortNumber: 1,
            Trips: createTrips(row2, noFlight)
        });
    }

    // Put everything together
    const sessionId = getSessionID();
    const data: API_CreateBooking.CreateBooking = {
        Booking: {
            Routes: routes
        }
    };

    const affiliateID = getUrlParam('a');
    if (affiliateID) data.Booking.AffiliateId = affiliateID;
    if (sessionId) data.Booking.SessionId = sessionId;

    const billingAccountId = getBillingAccountId();
    if (billingAccountId !== null) {
        data.AccountId = billingAccountId;
    }

    // Store session ID
    if (sessionId) setSessionItem('sessionId', sessionId);

    // Save session again
    saveSession();

    // Create booking
    return apiCall<API.NewBookingResponse>('POST', 'bookings/create', data).then(booking => {
        const bookingID = booking.data.uuid,
            accountID = booking.data.account_id;

        // Store account ID
        if (accountID) setSessionItem('accountId', accountID);

        // Success, go to step 2
        redirectToPage(`/transfer/?${queryfy({ id: bookingID })}`);
    }, (errorResponse: AjaxError) => getErrorMessages().then(errors => {
        const response = errorResponse.response as {
            message?: string,
            data?: string[],
            BookingData?: IBookingData
        };

        const noRoute = response && response.message === 'NO_ROUTE_FOUND';
        const noOptions = response.data && response.data.length && response.data[0] === 'No options available';
        const departureTimeInPast = response.message === 'DEPARTURE_TIME_IN_PAST';
        data.Booking.PickupTimeLocal = response.BookingData?.PickupTimeLocal ?? '';
        data.Booking.PickupTimeZulu = response.BookingData?.PickupTimeZulu ?? '';
        data.Booking.DropOffTimeLocal = response.BookingData?.DropOffTimeLocal ?? '';
        data.Booking.DropOffTimeZulu = response.BookingData?.DropOffTimeZulu ?? '';
        if (departureTimeInPast) {
            setGlobalError(errors.pickupTimeInPast);
            return;
        }

        if (noRoute || noOptions) {
            sendToQuoteRequestPage(data);
            return;
        }

        if (!errorsExisting()) {
            setGlobalError(errors.booking.failed);
        }
    })).finally(() => {
        $searchBtn.removeClass('loading');
        inProgress = false;
        return updateErrorView();
    });
}

async function sendToQuoteRequestPage(
    data: API_CreateBooking.CreateBooking
): Bluebird<void> {
    const { useUTC } = await fetchUserConfig();

    const PICKUP_FMT = 'YYYY-MM-DDTHH:mm:ss';
    const bookingLocal = data.Booking?.PickupTimeLocal;
    const bookingZulu = data.Booking?.PickupTimeZulu;
    const offsetMinutes = bookingLocal && bookingZulu
        ? moment(bookingLocal).diff(moment(bookingZulu), 'minutes')
        : 0;

    const request = flatMap(data.Booking.Routes, route => {
        return route.Trips.map(trip => {
            const from = mapPoint(trip.Pickup);
            const to = mapPoint(trip.Dropoff);

            if (!apiPointIsFlight(trip.Pickup) && !apiPointIsFlight(trip.Dropoff)) {
                // Point to point
                const entered = (trip.Pickup as API_CreateBooking.Pickup).PickupTime;
                const pickupTimeLocal = !entered
                    ? bookingLocal
                    : useUTC
                        ? moment(entered, PICKUP_FMT).add(offsetMinutes, 'minutes').format(PICKUP_FMT)
                        : entered;
                const pickupTimeZulu = !entered
                    ? bookingZulu
                    : useUTC
                        ? entered
                        : moment(entered, PICKUP_FMT).subtract(offsetMinutes, 'minutes').format(PICKUP_FMT);

                return {
                    from: {
                        ...from as Point,
                        pickupTime: pickupTimeLocal,
                        pickupTimeZulu
                    },
                    PickupTimeZulu: pickupTimeZulu,
                    PickupTimeLocal: pickupTimeLocal,
                    to: to as Point
                };
            } else {
                // Airport pickup or drop off
                if (apiPointIsFlight(trip.Pickup)) {
                    return {
                        PickupTimeZulu: data.Booking?.PickupTimeZulu ? data.Booking.PickupTimeZulu : '',
                        PickupTimeLocal: data.Booking?.PickupTimeLocal ? data.Booking.PickupTimeLocal : '',
                        DropOffTimeZulu: data.Booking?.DropOffTimeZulu ? data.Booking.DropOffTimeZulu : '',
                        DropofTimeLocal: data.Booking?.DropOffTimeLocal ? data.Booking.DropOffTimeLocal : '',
                        from: from as IFlight,
                        to: to as Point
                    };
                } else if (apiPointIsFlight(trip.Dropoff)) {
                    return {
                        PickupTimeZulu: data.Booking?.PickupTimeZulu ? data.Booking.PickupTimeZulu : '',
                        PickupTimeLocal: data.Booking?.PickupTimeLocal ? data.Booking.PickupTimeLocal : '',
                        DropOffTimeZulu: data.Booking?.DropOffTimeZulu ? data.Booking.DropOffTimeZulu : '',
                        DropofTimeLocal: data.Booking?.DropOffTimeLocal ? data.Booking.DropOffTimeLocal : '',
                        from: from as Point,
                        to: to as IFlight
                    };
                }

                throw new Error('Unable to find flight ID');
            }
        });
    });

    // Redirect to quote page

    const str = JSON.stringify(request);
    const base64 = encodeBase64(str);
    const uriParam = encodeURIComponent(base64);

    redirectToPage(`/quote?request=${uriParam}`);
}

function redirectToPage(page: string): void {
    const widgetOptions = window.GETE ? window.GETE.widgetOptions : null;

    const toolURL = widgetOptions
        ? widgetOptions.src
        : isExternal
            ? 'https://book.get-e.com'
            : host;

    const lang = (widgetOptions
        ? widgetOptions.language
        : pageLang) || 'en';

    if (lang) page = '/' + lang + page;
    if (isExternal) page = toolURL + page;

    if (widgetOptions && widgetOptions.useIframe) {
        // Create an iFrame
        const opts = widgetOptions.iframeOptions;

        const $toggles = $(`.${opts.toggleClass}`),
            $html = $('html');

        page += '&' + queryfy({
            whitelabel: true,
            noheader: opts.noheader,
            currency: widgetOptions.currency
        });

        $html.addClass('gete-booking-in-progress');

        $toggles.hide();
        let $iframe = $('#' + opts.id);
        if (!$iframe.length) {
            $iframe = $(`<iframe id="${opts.id}">`);
            $iframe.prop('src', page);
            $('body').append($iframe);
        } else {
            $iframe.prop('src', page);
            $iframe.show();
        }

        onMessage(() => {
            $toggles.show();
            $iframe.hide();
            $html.removeClass('gete-booking-in-progress');
        }, 'close-iframe');

    } else {
        // Redirect on same domain
        window.location.href = page;
    }
}

function mapPoint(apiPoint:
    API_CreateBooking.Place
    | API_CreateBooking.Flight
    | API_CreateBooking.TransferPoint
): Point | IFlight {
    if (apiPointIsPlace(apiPoint)) {
        const address = getPlaceInfo(apiPoint.PlaceID).name;

        return {
            type: 'Place',
            name: address
        };
    } else if (apiPointIsFlight(apiPoint)) {
        const flight = getFlightInfo(apiPoint.Airport.FlightId);
        return {
            type: 'Flight',
            flight: {
                number: flight.number,
                departureDateTime: flight.departureMoment,
                departureDateTimeZulu: flight.departureZuluMoment,
                arrivalDateTime: flight.arrivalMoment,
                arrivalDateTimeZulu: flight.arrivalZuluMoment,
                departureAirport: flight.departureAirport,
                arrivalAirport: flight.arrivalAirport
            }
        };
    } else if (apiPointIsAirport(apiPoint)) {
        const airportName = getTransportInfo(apiPoint.TransferPoint.Id).name;

        return {
            type: 'Airport',
            name: airportName
        };
    } else if (apiPointIsTrainStation(apiPoint)) {
        const trainStationName = getTransportInfo(apiPoint.TransferPoint.Id).name;
        return {
            type: 'TrainStation',
            name: trainStationName
        };
    } else {
        console.error(apiPoint);
        throw new TypeError('Unknown point');
    }
}

function apiPointIsPlace(point:
    API_CreateBooking.Place
    | API_CreateBooking.Flight
    | API_CreateBooking.TransferPoint
): point is API_CreateBooking.Place {
    return 'PlaceID' in point && typeof point.PlaceID === 'string';
}

function apiPointIsFlight(point:
    API_CreateBooking.Place
    | API_CreateBooking.Flight
    | API_CreateBooking.TransferPoint
): point is API_CreateBooking.Flight {
    return 'Airport' in point && typeof point.Airport.FlightId === 'number';
}

function apiPointIsAirport(point:
    API_CreateBooking.Place
    | API_CreateBooking.Flight
    | API_CreateBooking.TransferPoint
): point is API_CreateBooking.Airport {
    return ('TransferPoint' in point) && point.TransferPoint.Type === 'Airport';
}

function apiPointIsTrainStation(point:
    API_CreateBooking.Place
    | API_CreateBooking.Flight
    | API_CreateBooking.TransferPoint
): point is API_CreateBooking.TrainStation {
    return ('TransferPoint' in point) && point.TransferPoint.Type === 'Trainstation';
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/assets/scripts/modules/widget-core/submit/submit.ts